import React from "react";
import { transformColor } from "./generalUtils";
import {
  isJsPattern,
  getJsPatternComponent,
} from "../../renderer/patterns-js";

// Constants for background patterns
export const DEFAULT_BACKGROUND_PATTERN = "solid-primary";
export const DEFAULT_EXTRA_CLASSES = "absolute inset-0 w-full h-full -z-10";
export const DEFAULT_EXTRA_IMAGE_CLASSES = "bg-cover bg-center";

// Legacy/deprecated pattern keys that might still exist in persisted data.
// We normalize them so old sites keep rendering correctly after renames/removals.
const LEGACY_BACKGROUND_PATTERN_ALIASES = {
  // Renames
  "night-clouds-moon-stars": "night-clouds",
  "clouds-and-sun": "cloudy-day",
  "quantum-grid": "water-lines",
  "cyberpunk-neon-grid": "road-runner",
  // Wave → Blur renames
  "waves": "blur",
  "wave-flow": "blur-2",
  "waves-spectrum": "blur-3",
  // Removed patterns (fallback to default)
  "star-pattern": DEFAULT_BACKGROUND_PATTERN,
  "starfield": DEFAULT_BACKGROUND_PATTERN,
  "neon-hex": DEFAULT_BACKGROUND_PATTERN,
  "plasma-blend": DEFAULT_BACKGROUND_PATTERN,
  "argyle": DEFAULT_BACKGROUND_PATTERN,
  "sliding-lines": DEFAULT_BACKGROUND_PATTERN,
};

function normalizeBackgroundPatternKey(patternKey) {
  if (!patternKey) return DEFAULT_BACKGROUND_PATTERN;
  return LEGACY_BACKGROUND_PATTERN_ALIASES[patternKey] || patternKey;
}

// Constants for pattern limits
const BRIGHTNESS_MIN = 0;
const BRIGHTNESS_MAX = 200;
const OPACITY_MIN = 0;
const OPACITY_MAX = 1;
const MAX_COLORS = 4;

/**
 * Creates a background element based on whether it's an image or pattern
 */
export function getBackground({
  bgImageUrl,
  imageStyleClasses,
  imageInlineStyles,
  bgPatternClass,
  bgPatternStyle,
  bgPatternJsLayer = null,
  extraClasses = DEFAULT_EXTRA_CLASSES,
  extraImageClasses = DEFAULT_EXTRA_IMAGE_CLASSES,
  extraPatternClasses = "",
}) {
  if (bgImageUrl) {
    return (
      <div
        className={`${imageStyleClasses} ${extraClasses} ${extraImageClasses}`}
        style={{ backgroundImage: `url(${bgImageUrl})`, ...imageInlineStyles }}
      />
    );
  }

  return (
    <div
      className={`${bgPatternClass} ${extraClasses} ${extraPatternClasses}`}
      style={bgPatternStyle}
    >
      {bgPatternJsLayer}
    </div>
  );
}

/**
 * Applies brightness adjustment to pattern class if needed
 */
function applyBrightnessClass(pattern, colorClass) {
  if (typeof pattern.brightness !== "number" || pattern.brightness === 100) {
    return colorClass;
  }

  const brightness = Math.min(
    BRIGHTNESS_MAX,
    Math.max(BRIGHTNESS_MIN, pattern.brightness),
  );
  return `${colorClass} filter brightness-[${brightness}%]`;
}

/**
 * Applies opacity adjustment to pattern class if needed
 */
function applyOpacityClass(pattern, colorClass) {
  if (typeof pattern.opacity !== "number") {
    return colorClass;
  }

  const opacity = Math.min(
    OPACITY_MAX,
    Math.max(OPACITY_MIN, pattern.opacity / 100),
  );
  return `${colorClass} opacity-[${opacity}]`;
}

/**
 * Processes pattern colors into style object
 */
function processPatternColors(colors) {
  if (!colors || !Array.isArray(colors)) {
    return {};
  }

  return colors.slice(0, MAX_COLORS).reduce(
    (style, color, index) => ({
      ...style,
      [`--color-${index + 1}`]: transformColor(color, 100, "hex"),
    }),
    {},
  );
}

/**
 * Builds the JS pattern layer (a React element) for patterns whose visuals
 * are powered by JavaScript instead of (or in addition to) CSS.
 *
 * Returns `null` for CSS-only patterns. Consumers render the result as a
 * child of the pattern <div> so it inherits the CSS-var color shell,
 * positioning, brightness/opacity utilities, etc.
 */
function buildJsPatternLayer(patternKey, pattern) {
  if (!isJsPattern(patternKey)) return null;
  const Component = getJsPatternComponent(patternKey);
  if (!Component) return null;

  const normalizedColors = Array.isArray(pattern?.colors)
    ? pattern.colors
        .slice(0, MAX_COLORS)
        .map((c) => {
          try {
            return transformColor(c, 100, "hex");
          } catch (err) {
            return null;
          }
        })
        .filter(Boolean)
    : [];

  return React.createElement(Component, {
    colors: normalizedColors,
    brightness: typeof pattern?.brightness === "number" ? pattern.brightness : 100,
    opacity: typeof pattern?.opacity === "number" ? pattern.opacity : 100,
    patternKey,
  });
}

/**
 * Gets background pattern classes and styles based on pattern configuration.
 *
 * Returns:
 *   - `colorClass`: tailwind/utility class string for the pattern <div>
 *   - `colorStyle`: inline style with --color-1..4 CSS custom properties
 *   - `jsPatternLayer`: React element for JS-powered patterns (null for CSS-only)
 *
 * The `jsPatternLayer` is intended to be rendered as a child of the pattern
 * <div>, so the CSS color shell + brightness/opacity utilities still apply.
 */
export function getBackgroundPatternClass(pattern) {
  if (!pattern) {
    return { colorStyle: {}, colorClass: "", jsPatternLayer: null };
  }

  const patternKey = normalizeBackgroundPatternKey(pattern.pattern);
  const colorStyle = processPatternColors(pattern.colors);

  const withBrightness = applyBrightnessClass(pattern, patternKey);
  const withOpacity = applyOpacityClass(pattern, withBrightness);

  const jsPatternLayer = buildJsPatternLayer(patternKey, pattern);

  return {
    colorStyle,
    colorClass: withOpacity,
    jsPatternLayer,
  };
}
